import { useEffect, useState } from "react";
import { http, apiError, nf, rp, rpShort } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, KatBadge } from "@/components/Ui";
import { Save, TrendingUp, Plus, Trash2, Pencil, PlayCircle, UploadCloud, Download, Loader2, X } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const empty = {
  kode_stock: "", deskripsi_produk: "", type_produk: "", spesifikasi_produk: "",
  qty_permintaan: "", harga_jual_a: "", total_nominal_proyeksi: "",
  estimasi_waktu_kebutuhan: "", catatan: "", kategori_pemasaran: "",
};

const inp = "w-full h-10 px-3 border border-neutral-300 bg-white outline-none focus:ring-2 focus:ring-[#4338CA] focus:border-transparent text-sm";
const lbl = "text-[11px] font-semibold uppercase tracking-wide text-[#525252]";

export default function ProjectionEntry() {
  const [f, setF] = useState(empty);
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState("");
  const [sheet, setSheet] = useState(null);
  const [editIdx, setEditIdx] = useState(-1);

  useEffect(() => {
    http.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const autoTotal = (qty, harga) => (Number(qty) || 0) * (Number(harga) || 0);

  const autofill = async (kode) => {
    if (!kode) return;
    try {
      const { data } = await http.get(`/products/${kode.toUpperCase()}`);
      setF((s) => ({
        ...s,
        deskripsi_produk: data.deskripsi_produk || "",
        type_produk: data.type_produk || "",
        spesifikasi_produk: data.spesifikasi_produk || "",
        harga_jual_a: s.harga_jual_a || data.harga_jual_a || "",
        kategori_pemasaran: data.kategori_pemasaran || "",
        total_nominal_proyeksi: autoTotal(s.qty_permintaan, s.harga_jual_a || data.harga_jual_a),
      }));
      setInfo(data);
      toast.success(`Data produk ${kode.toUpperCase()} terisi otomatis`);
    } catch {
      setInfo(null);
      toast.error("Kode stock belum terdaftar");
    }
  };

  const setQty = (v) => setF((s) => ({ ...s, qty_permintaan: v, total_nominal_proyeksi: autoTotal(v, s.harga_jual_a) }));
  const setHarga = (v) => setF((s) => ({ ...s, harga_jual_a: v, total_nominal_proyeksi: autoTotal(s.qty_permintaan, v) }));

  const addRow = (e) => {
    e.preventDefault();
    if (!f.kode_stock || !f.qty_permintaan) {
      toast.error("Kode Stock dan Qty Permintaan wajib diisi");
      return;
    }
    const row = {
      ...f,
      kode_stock: f.kode_stock.toUpperCase(),
      qty_permintaan: Number(f.qty_permintaan) || 0,
      harga_jual_a: Number(f.harga_jual_a) || 0,
      total_nominal_proyeksi: Number(f.total_nominal_proyeksi) || autoTotal(f.qty_permintaan, f.harga_jual_a),
    };
    setRows((r) => (editIdx >= 0 ? r.map((x, i) => (i === editIdx ? row : x)) : [...r, { ...row, _src: "manual" }]));
    toast.success(editIdx >= 0 ? "Baris diperbarui" : "Ditambahkan ke daftar pengajuan");
    setF(empty);
    setInfo(null);
    setEditIdx(-1);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/projection/parse", fd);
      if (!data.rows.length) {
        toast.error("Tidak ada baris terbaca. Pastikan file memiliki kolom 'Kode Stock' dan 'Qty Permintaan' — unduh Template untuk contoh format.");
        return;
      }
      const fileRows = data.rows.map((r) => ({ ...r, _src: "file" }));
      setRows((prev) => [...prev.filter((r) => r._src !== "file"), ...fileRows]);
      toast.success(`${fileRows.length} baris dibaca dari file. Klik PROSES untuk melihat total pengajuan.`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await http.get("/template/projection", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_projection.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal mengunduh template");
    }
  };

  const totalQty = rows.reduce((a, r) => a + (Number(r.qty_permintaan) || 0), 0);
  const totalNominal = rows.reduce((a, r) => a + (Number(r.total_nominal_proyeksi) || 0), 0);

  const proses = () => {
    if (!rows.length) {
      toast.error("Belum ada baris untuk diproses");
      return;
    }
    const perKategori = {};
    rows.forEach((r) => {
      const k = r.kategori_pemasaran || "Belum Terkategori";
      perKategori[k] = perKategori[k] || { qty: 0, nominal: 0, item: 0 };
      perKategori[k].qty += Number(r.qty_permintaan) || 0;
      perKategori[k].nominal += Number(r.total_nominal_proyeksi) || 0;
      perKategori[k].item += 1;
    });
    setSheet({ item: rows.length, totalQty, totalNominal, perKategori });
  };

  const simpan = async () => {
    setBusy("save");
    try {
      const { data } = await http.post("/projection/batch", {
        rows: rows.map((r) => ({
          kode_stock: r.kode_stock,
          deskripsi_produk: r.deskripsi_produk || "",
          type_produk: r.type_produk || "",
          spesifikasi_produk: r.spesifikasi_produk || "",
          qty_permintaan: Number(r.qty_permintaan) || 0,
          harga_jual_a: Number(r.harga_jual_a) || 0,
          total_nominal_proyeksi: Number(r.total_nominal_proyeksi) || 0,
          estimasi_waktu_kebutuhan: r.estimasi_waktu_kebutuhan || "",
          catatan: r.catatan || "",
        })),
      });
      toast.success(`${data.saved} baris pengajuan tersimpan · Total ${rp(data.total_nominal)}`);
      setRows([]);
      setSheet(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const trend = info?.trend_12_bulan || [];
  const avg = trend.length ? trend.reduce((a, b) => a + b.qty, 0) / trend.length : 0;

  return (
    <div data-testid="projection-entry-page">
      <PageHeader subtitle="Kantor Cabang" title="Usulan Proyeksi Kebutuhan Material" testId="projection-entry-header" />

      <div className="p-6 sm:p-8 space-y-4">
        <div className="erp-panel p-5" data-testid="projection-upload-box">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <div className="mono text-[11px] uppercase tracking-widest text-[#525252]">
                Upload File Excel Proyeksi
              </div>
              <p className="text-sm text-[#525252] mt-1">
                Kolom: Kode Stock, Deskripsi, Type, Spesifikasi, Qty Permintaan,
                Harga Jual A, Total Nominal Proyeksi, Estimasi Waktu Kebutuhan. Data
                masuk ke daftar di bawah, lalu klik <strong>PROSES</strong> sebelum disimpan.
              </p>
            </div>
            <div className="flex gap-2">
              <button data-testid="projection-template-button" onClick={downloadTemplate}
                className="h-10 px-3 border border-neutral-800 text-xs uppercase tracking-wide flex items-center gap-2 hover:bg-neutral-900 hover:text-white transition-colors">
                <Download className="h-3.5 w-3.5" /> Template
              </button>
              <label data-testid="projection-file-button"
                className="h-10 px-4 bg-[#4338CA] text-white text-xs uppercase tracking-wide flex items-center gap-2 hover:bg-[#3730A3] transition-colors cursor-pointer">
                {busy === "upload" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
                Pilih File
                <input data-testid="projection-file-input" type="file" accept=".xlsx,.xls" className="hidden"
                  onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <form onSubmit={addRow} className="erp-panel p-5 space-y-5 lg:col-span-7" data-testid="projection-entry-form">
            <div className="mono text-[11px] uppercase tracking-widest text-[#525252]">
              {editIdx >= 0 ? "Revisi Baris Daftar" : "Entry Manual — isi Kode Stock & Qty, sisanya otomatis"}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Kode Stock</label>
                <input data-testid="projection-kode-input" required list="prodlist5" className={inp + " uppercase"}
                  value={f.kode_stock} onChange={(e) => setF({ ...f, kode_stock: e.target.value })}
                  onBlur={(e) => autofill(e.target.value)} placeholder="KSFO022" />
                <datalist id="prodlist5">
                  {products.map((p) => <option key={p.kode_stock} value={p.kode_stock}>{p.deskripsi_produk}</option>)}
                </datalist>
              </div>
              <div>
                <label className={lbl}>Qty Permintaan</label>
                <input data-testid="projection-qty-input" required type="number" className={inp + " mono text-right"}
                  value={f.qty_permintaan} onChange={(e) => setQty(e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={lbl}>Harga Jual A</label>
                <input data-testid="projection-harga-input" type="number" className={inp + " mono text-right"}
                  value={f.harga_jual_a} onChange={(e) => setHarga(e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={lbl}>Total Nominal Proyeksi</label>
                <input data-testid="projection-total-input" type="number" className={inp + " mono text-right"}
                  value={f.total_nominal_proyeksi}
                  onChange={(e) => setF({ ...f, total_nominal_proyeksi: e.target.value })} placeholder="0" />
                <div className="mono text-xs mt-1 text-[#4338CA]" data-testid="projection-total-preview">
                  {rp(f.total_nominal_proyeksi || autoTotal(f.qty_permintaan, f.harga_jual_a))}
                </div>
              </div>
              <div>
                <label className={lbl}>Deskripsi Produk</label>
                <input data-testid="projection-deskripsi-input" className={inp} value={f.deskripsi_produk}
                  onChange={(e) => setF({ ...f, deskripsi_produk: e.target.value })} />
              </div>
              <div>
                <label className={lbl}>Type Produk</label>
                <input data-testid="projection-type-input" className={inp} value={f.type_produk}
                  onChange={(e) => setF({ ...f, type_produk: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>Spesifikasi Produk</label>
                <input data-testid="projection-spec-input" className={inp} value={f.spesifikasi_produk}
                  onChange={(e) => setF({ ...f, spesifikasi_produk: e.target.value })} />
              </div>
              <div>
                <label className={lbl}>Estimasi Waktu Kebutuhan</label>
                <input data-testid="projection-waktu-input" className={inp} value={f.estimasi_waktu_kebutuhan}
                  onChange={(e) => setF({ ...f, estimasi_waktu_kebutuhan: e.target.value })} placeholder="Juli 2026" />
              </div>
              <div>
                <label className={lbl}>Catatan</label>
                <input data-testid="projection-catatan-input" className={inp} value={f.catatan}
                  onChange={(e) => setF({ ...f, catatan: e.target.value })} />
              </div>
            </div>

            <div className="flex gap-2">
              <button data-testid="projection-add-row-button"
                className="h-10 px-6 bg-[#0A0A0A] text-white text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#333] transition-colors">
                <Plus className="h-3.5 w-3.5" /> {editIdx >= 0 ? "Perbarui Baris" : "Tambah ke Daftar"}
              </button>
              {editIdx >= 0 && (
                <button type="button" data-testid="projection-cancel-edit-button"
                  onClick={() => { setF(empty); setEditIdx(-1); }}
                  className="h-10 px-4 border border-neutral-800 text-xs uppercase tracking-wide">
                  Batal
                </button>
              )}
            </div>
          </form>

          <div className="erp-panel p-5 lg:col-span-5" data-testid="projection-guidance-panel">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#4338CA]" />
              <span className="mono text-[11px] uppercase tracking-widest text-[#525252]">
                Petunjuk Trend Penjualan 12 Bulan
              </span>
            </div>

            {!info ? (
              <p className="text-sm text-[#525252] mt-4">
                Masukkan Kode Stock untuk melihat trend penjualan 12 bulan terakhir,
                harga jual A, dan kategori pemasaran produk.
              </p>
            ) : (
              <div className="mt-4 space-y-3">
                <div>
                  <div className="mono text-sm font-semibold">{info.kode_stock}</div>
                  <div className="text-xs text-[#525252]">{info.deskripsi_produk} · {info.type_produk}</div>
                </div>
                <div>
                  <div className={lbl}>Kategori Pemasaran Produk</div>
                  <div className="mt-1" data-testid="projection-kategori-info">
                    <KatBadge value={info.kategori_pemasaran} />
                  </div>
                </div>
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend}>
                      <CartesianGrid stroke="#eee" vertical={false} />
                      <XAxis dataKey="periode" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={50} interval={0} />
                      <YAxis tick={{ fontSize: 9 }} width={45} />
                      <Tooltip formatter={(v) => nf(v)} />
                      <Line type="monotone" dataKey="qty" stroke="#4338CA" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="border-t border-neutral-200 pt-3 text-sm">
                  Rata-rata penjualan bulanan cabang:{" "}
                  <span className="mono font-semibold" data-testid="projection-avg-info">{nf(Math.round(avg))}</span>
                  <div className="text-xs text-[#525252] mt-1">
                    Saran qty untuk kebutuhan 2 bulan: <span className="mono">{nf(Math.round(avg * 2))}</span>
                    {info.harga_jual_a ? <> · Harga Jual A: <span className="mono">{rp(info.harga_jual_a)}</span></> : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="erp-panel" data-testid="projection-draft-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200">
            <div className="mono text-[11px] uppercase tracking-widest text-[#525252]">
              Daftar Pengajuan Belum Tersimpan ({rows.length} baris)
            </div>
            <div className="flex gap-2">
              {rows.length > 0 && (
                <button data-testid="projection-clear-button" onClick={() => setRows([])}
                  className="h-10 px-3 border border-neutral-800 text-xs uppercase tracking-wide flex items-center gap-2 hover:bg-neutral-900 hover:text-white transition-colors">
                  <Trash2 className="h-3.5 w-3.5" /> Kosongkan
                </button>
              )}
              <button data-testid="projection-proses-button" onClick={proses} disabled={!rows.length}
                className="h-10 px-6 bg-[#4338CA] text-white text-xs uppercase tracking-wide flex items-center gap-2 hover:bg-[#3730A3] transition-colors disabled:opacity-50">
                <PlayCircle className="h-3.5 w-3.5" /> Proses
              </button>
            </div>
          </div>

          <div className="overflow-auto max-h-[420px]">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Kode Stock</th><th>Deskripsi</th><th>Type</th><th>Spesifikasi</th>
                  <th className="num">Qty Permintaan</th><th className="num">Harga Jual A</th>
                  <th className="num">Total Nominal Proyeksi</th><th>Estimasi Waktu</th>
                  <th>Kategori</th><th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} data-testid={`projection-draft-row-${r.kode_stock}`}>
                    <td className="mono font-medium">{r.kode_stock}</td>
                    <td className="max-w-[180px] truncate">{r.deskripsi_produk}</td>
                    <td>{r.type_produk}</td>
                    <td className="max-w-[180px] truncate">{r.spesifikasi_produk}</td>
                    <td className="num">{nf(r.qty_permintaan)}</td>
                    <td className="num">{rp(r.harga_jual_a)}</td>
                    <td className="num font-semibold">{rp(r.total_nominal_proyeksi)}</td>
                    <td>{r.estimasi_waktu_kebutuhan}</td>
                    <td><KatBadge value={r.kategori_pemasaran} /></td>
                    <td>
                      <div className="flex gap-1">
                        <button data-testid={`projection-edit-row-${i}`} title="Revisi baris"
                          onClick={() => { setF({ ...empty, ...r }); setEditIdx(i); }}
                          className="p-1 border border-neutral-300 hover:bg-neutral-900 hover:text-white transition-colors">
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button data-testid={`projection-delete-row-${i}`} title="Hapus baris"
                          onClick={() => setRows(rows.filter((_, x) => x !== i))}
                          className="p-1 border border-[#FF3B30] text-[#b91c1c] hover:bg-[#FF3B30] hover:text-white transition-colors">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-neutral-500 py-6">
                    Belum ada baris. Upload file Excel atau tambahkan lewat entry manual.
                  </td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4}>TOTAL</td>
                    <td className="num" data-testid="projection-draft-total-qty">{nf(totalQty)}</td>
                    <td className="num">—</td>
                    <td className="num" data-testid="projection-draft-total-nominal">{rp(totalNominal)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {sheet && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="projection-summary-modal">
          <div className="bg-white w-full max-w-lg border border-neutral-300 fade-up">
            <div className="flex items-center justify-between bg-[#0A0A0A] px-5 py-3">
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-[#A5B4FC]">
                  Lembar Total Nilai Pengajuan
                </div>
                <div className="font-display text-white text-lg font-bold">Konfirmasi Proyeksi</div>
              </div>
              <button data-testid="projection-summary-close" onClick={() => setSheet(null)} className="text-white/70 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-neutral-200 p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-[#525252]">Jumlah Item</div>
                  <div className="mono text-xl font-semibold" data-testid="summary-item">{nf(sheet.item)}</div>
                </div>
                <div className="border border-neutral-200 p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-[#525252]">Total Qty</div>
                  <div className="mono text-xl font-semibold" data-testid="summary-qty">{nf(sheet.totalQty)}</div>
                </div>
                <div className="border border-neutral-200 p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-[#525252]">Total Nilai</div>
                  <div className="mono text-xl font-semibold text-[#4338CA]" data-testid="summary-nominal">{rpShort(sheet.totalNominal)}</div>
                </div>
              </div>

              <div>
                <div className={lbl}>Rincian per Kategori Pemasaran</div>
                <table className="erp-table mt-2">
                  <thead>
                    <tr><th>Kategori</th><th className="num">Item</th><th className="num">Qty</th><th className="num">Nilai</th></tr>
                  </thead>
                  <tbody>
                    {Object.entries(sheet.perKategori).map(([k, v]) => (
                      <tr key={k}>
                        <td>{k}</td>
                        <td className="num">{nf(v.item)}</td>
                        <td className="num">{nf(v.qty)}</td>
                        <td className="num">{rp(v.nominal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-neutral-200 pt-3 text-sm">
                Total nilai pengajuan: <strong className="mono">{rp(sheet.totalNominal)}</strong>.
                Jika sudah sesuai klik <strong>Save</strong>, jika perlu revisi klik <strong>Edit</strong>.
              </div>

              <div className="flex gap-2 justify-end">
                <button data-testid="projection-edit-button" onClick={() => setSheet(null)}
                  className="h-10 px-5 border border-neutral-800 text-xs uppercase tracking-wide flex items-center gap-2 hover:bg-neutral-900 hover:text-white transition-colors">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button data-testid="projection-save-button" onClick={simpan} disabled={busy === "save"}
                  className="h-10 px-6 bg-[#16A34A] text-white text-xs uppercase tracking-wide flex items-center gap-2 hover:bg-[#15803d] transition-colors disabled:opacity-60">
                  {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
